import React, {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import cn from "classnames";

import styles from "./Drawer.module.scss";
import { lockBodyScroll } from "Shared/Utils/scrollLock";

const CLOSE_ANIMATION_MS = 200;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Muted line under the title. */
  subtitle?: ReactNode;
  /** Shown above the title, e.g. a status badge. */
  eyebrow?: ReactNode;
  /** Icon buttons next to the close button. */
  actions?: ReactNode;
  /** Sticky action bar at the bottom. */
  footer?: ReactNode;
  children: ReactNode;
  /** Accessible name when the title isn't plain text. */
  "aria-label"?: string;
  /** Accessible name of the close button. */
  closeLabel?: string;
  width?: number;
  /**
   * False keeps it open on Escape and backdrop clicks, for forms where a
   * stray click would lose input; the close button still works.
   */
  dismissible?: boolean;
  /** For an opener's `aria-controls`. */
  id?: string;
};

/**
 * Right-side panel over the page. Closes on Escape, backdrop click or the
 * close button; keeps keyboard focus inside while open and returns it to
 * the element that opened it. Dialogs opened from inside (MUI modals)
 * stack above it.
 */
const Drawer = ({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  actions,
  footer,
  children,
  "aria-label": ariaLabel,
  closeLabel = "Close",
  width = 460,
  dismissible = true,
  id,
}: DrawerProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  // Keep the panel mounted while it slides out.
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, CLOSE_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Focus the panel on open and return focus to the opener on close.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open]);

  // Stop the page behind from scrolling.
  useEffect(() => (open ? lockBodyScroll() : undefined), [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && dismissible) {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    ).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Render as soon as it opens, so focus can move in on the same commit.
  if (!open && !mounted) return null;

  return createPortal(
    <div className={cn(styles.drawer, closing && styles.drawer_closing)}>
      <div
        className={styles.backdrop}
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        id={id}
        className={styles.panel}
        style={{ width: `min(${width}px, 100vw)` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel ? undefined : titleId}
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.eyebrow}>{eyebrow}</div>
            <div className={styles.actions}>
              {actions}
              <button
                type="button"
                className={styles.iconButton}
                aria-label={closeLabel}
                onClick={onClose}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          </div>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
};

/** Square icon button for the drawer header `actions`. */
export const DrawerIconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    className={styles.iconButton}
    aria-label={label}
    title={label}
    onClick={onClick}
  >
    {children}
  </button>
);

/** Full-width footer button; `primary` for the main action. */
export const DrawerButton = ({
  variant = "secondary",
  icon,
  children,
  onClick,
  disabled,
}: {
  variant?: "primary" | "secondary";
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    className={cn(styles.button, styles[`button_${variant}`])}
    onClick={onClick}
    disabled={disabled}
  >
    {icon}
    {children}
  </button>
);

/** Titled block inside the drawer body. */
export const DrawerSection = ({
  title,
  action,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

/** Label/value pairs in a two-column grid. */
export const DrawerFacts = ({
  items,
}: {
  items: { label: string; value: ReactNode }[];
}) => (
  <dl className={styles.facts}>
    {items.map((item) => (
      <div key={item.label} className={styles.fact}>
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

export default Drawer;

/** Subtitle item: optional icon + text, e.g. the vehicle or a "#41" reference. */
export const DrawerMeta = ({
  icon,
  muted = false,
  children,
}: {
  icon?: ReactNode;
  muted?: boolean;
  children: ReactNode;
}) => (
  <span className={cn(styles.meta, muted && styles.meta_muted)}>
    {icon}
    {children}
  </span>
);

/** Placeholder lines while the record loads. */
export const DrawerLoading = ({ lines = 6 }: { lines?: number }) => (
  <div className={styles.loading} aria-busy="true" aria-live="polite">
    {Array.from({ length: lines }, (_, index) => (
      <span
        key={index}
        className={styles.skeleton}
        style={{ width: `${55 + ((index * 23) % 40)}%` }}
      />
    ))}
  </div>
);

/** Paragraph of free text (descriptions, notes); keeps line breaks. */
export const DrawerText = ({ children }: { children: ReactNode }) => (
  <p className={styles.text}>{children}</p>
);

/** Lays footer buttons side by side. */
export const DrawerButtonRow = ({ children }: { children: ReactNode }) => (
  <div className={styles.buttonRow}>{children}</div>
);
